import type { EnvHealthAlert, EnvHealthProcessingCategory, EnvHealthResponse } from '@customerio/hydra-ui-api';
import { DATA_ALERT_SEVERITY_RANK } from './constants';
import { isEmptyString } from '@/utils/string';
import { generatePath } from '@/services/router/generate-path';
import { SQL_IMPORT_DATABASES } from '@/services/db/sql-import/entity';

export const CATEGORY_LABELS: Record<string, string> = {
  identify: 'Profile updates',
  events: 'Incoming events',
  campaigns: 'Campaign processing',
  broadcasts: 'One-time sends',
  transactional: 'Transactional processing',
  delivery_metrics: 'Delivery metrics',
  csv_imports: 'CSV imports',
  integrations: 'Integrations',
  objects: 'Custom objects',
  segments: 'Segment updates',
};

export const EGRESS_CATEGORY_LABELS: Record<string, string> = {
  message_delivery: 'Message delivery',
  transactional_delivery: 'Transactional delivery',
  webhooks: 'Webhooks',
  journey_actions: 'Journey actions',
  alias: 'Profile merging',
  related_updates: 'Related data updates',
};

export const SERVICE_INFO_TOOLTIPS: Record<string, string> = {
  identify: 'Profile and identity data from your app, SDK, or API.',
  events: 'Custom events sent for segmentation and journey triggers.',
  campaigns: 'Campaign processing: starting, continuing, and retrying campaign workflows.',
  broadcasts: 'Indicates health of sending for newsletters and anonymous in-app messages.',
  transactional: 'Prepare and evaluate transactional messages before sending.',
  delivery_metrics:
    'Delivery metrics processing: tracks sent, opened, clicked, bounced, and complaint events, not message delivery itself.',
  csv_imports: 'Profiles or events imported via CSV upload.',
  integrations: 'Data synced from connected integrations and Reverse ETL.',
  objects: 'Custom object data, relationships, and object-based segment conditions.',
  segments: 'Static segment changes and scheduled condition re-evaluation.',
  message_delivery: 'Campaign and broadcast delivery to email, push, and SMS.',
  transactional_delivery: 'Transactional message delivery to channels.',
  webhooks: 'Outbound webhook action calls from journeys.',
  journey_actions: 'Data lookup and profile update steps within journeys.',
  alias: 'Profile merge and anonymous event resolution.',
  related_updates: 'Cascading updates from object and relationship changes.',
};

export const CATEGORY_IMPACT: Record<string, string> = {
  identify: 'Profile updates may not appear immediately. Segments and filters could show stale data.',
  events: 'Incoming events are queued. Event-triggered journeys and workflows may fire late.',
  campaigns: 'Campaigns may take longer to progress to the next step.',
  broadcasts: 'One-time sends are backed up. Delivery will be slower than expected.',
  transactional: 'Incoming transactional messages are backed up. Subsequent delivery may be delayed.',
  delivery_metrics: 'Delivery metric processing is slow. Sent, open, and click counts may be temporarily inaccurate.',
  csv_imports: 'CSV imports are still processing. Imported profiles and attributes are not yet available.',
  integrations: 'Integration imports are still processing. Imported profiles are not yet available.',
  objects:
    'Custom object updates are pending. Relationships and object data may be outdated, and segments using object conditions may lag behind.',
  segments:
    'Segment membership updates are behind. Static segment changes and scheduled re-evaluations may not reflect recent data.',
  message_delivery: 'Emails, push notifications, and SMS may arrive late.',
  transactional_delivery: 'Transactional messages (e.g. password resets) are waiting to be sent.',
  webhooks: 'Webhook requests from journeys may be delayed.',
  journey_actions: 'Journeys may be delayed at data lookup and profile update steps.',
  alias: 'Profile merging is delayed. Some duplicate profiles may appear temporarily.',
  related_updates: 'Updates triggered by object and relationship changes may not reflect recent data.',
};

export function formatThroughput(perSecond: number): string {
  if (perSecond <= 0) return '0/s';
  if (perSecond >= 1) return `${Math.round(perSecond).toLocaleString()}/s`;
  const perMinute = perSecond * 60;
  if (perMinute >= 1) return `${Math.round(perMinute)}/min`;
  return '<1/min';
}

export const SEVERITY_COLOR: Record<string, 'yellow' | 'red' | 'grey'> = {
  caution: 'yellow',
  error: 'red',
  info: 'grey',
};

/** Maps push health config check statuses to StatusIndicator colors. */
export const CONFIG_STATUS_COLOR: Record<string, 'green' | 'red' | 'yellow'> = {
  ok: 'green',
  missing: 'red',
  warning: 'yellow',
};

export const DELAY_STATUS_LABELS = {
  normal: 'Normal',
  slight_delay: 'Slight delay',
  delayed: 'Delayed',
  major_delay: 'Major delay',
};

export type DelayTier = 'normal' | 'slight_delay' | 'delayed' | 'major_delay';

const DELAY_TIER_COLORS: Record<DelayTier, 'green' | 'yellow' | 'red'> = {
  normal: 'green',
  slight_delay: 'yellow',
  delayed: 'yellow',
  major_delay: 'red',
};

/**
 * Whether a delay is the kind of thing worth raising an alarm about.
 *
 * An absent cause counts as one. The backend omits `delay_cause` when it had no
 * throughput baseline to attribute the delay against, and it treats that same
 * absence as severe, so a delay we cannot explain must not be softened. Any
 * unrecognised value lands here too, which keeps a future cause alarming until
 * someone decides otherwise.
 */
export function isFaultDelayCause(cause: string | null | undefined): boolean {
  return cause !== 'volume' && cause !== 'rate_limited';
}

/**
 * Colour is an alarm level; the label carries the magnitude.
 *
 * "Major delay" and "5 hr 12 min behind" say how far behind a queue is, and they
 * stay factual whatever the reason. Red says something is wrong. A workspace
 * steadily working through its own send volume, or one being paced on purpose, is
 * not wrong however far behind it gets, so the chip caps at yellow there rather
 * than contradicting the calm summary at the top of the page.
 */
function tierColor(tier: DelayTier, cause: string | null | undefined): 'green' | 'yellow' | 'red' {
  if (tier === 'major_delay' && !isFaultDelayCause(cause)) return 'yellow';
  return DELAY_TIER_COLORS[tier];
}

/**
 * Badge's semantic colours, keyed off the colour already resolved above rather
 * than off the tier.
 *
 * Deriving from the resolved colour is what keeps the two in step. That colour is
 * capped by cause, so a backlog that is expected shows a caution badge beside a
 * caution indicator. Keying off the tier instead would put a critical badge next
 * to a yellow dot on exactly the rows this page set out to stop alarming people
 * about.
 */
const DELAY_BADGE_COLORS: Record<'green' | 'yellow' | 'red', 'success' | 'caution' | 'critical'> = {
  green: 'success',
  yellow: 'caution',
  red: 'critical',
};

/**
 * Severity order for the four tiers. Colour alone cannot order them, because
 * `slight_delay` and `delayed` share yellow, and raw seconds cannot either now
 * that the backend scales its thresholds: a category can be a worse tier on
 * fewer seconds than another.
 */
export const DELAY_TIER_RANK: Record<DelayTier, number> = {
  normal: 0,
  slight_delay: 1,
  delayed: 2,
  major_delay: 3,
};

/**
 * The tier is an open enum on the wire. An unrecognised value must not index
 * straight into the label and colour maps, or the status cell renders blank —
 * the one outcome the fallback exists to prevent.
 */
function isDelayTier(value: unknown): value is DelayTier {
  return typeof value === 'string' && value in DELAY_TIER_RANK;
}

/**
 * The one place a tier is still derived from raw seconds, and only when the
 * response did not carry `delay_tier` — a payload cached before the field
 * existed, or a region still serving the older shape. Everything else reads the
 * backend's tier.
 *
 * These boundaries are deliberately not documented in the UI. They are a
 * stand-in for the backend's own thresholds, which are moving to scale with
 * workspace size, so a small workspace and a large one will not agree on what
 * "delayed" means. Printing these numbers would state a rule the backend no
 * longer follows.
 */
function deriveDelayTier(delaySeconds: number | null | undefined): DelayTier {
  if (delaySeconds == null || delaySeconds < 10) return 'normal';
  if (delaySeconds < 60) return 'slight_delay';
  if (delaySeconds < 300) return 'delayed';
  return 'major_delay';
}

type DelayStatusInput = {
  delay_seconds?: number | null;
  pending: number;
  /** Typed loosely on purpose: the wire value may be a tier this build does not know. */
  delay_tier?: string | null;
  /** Loose for the same reason, and an unknown cause is treated as a fault. */
  delay_cause?: string | null;
};

/**
 * An empty queue has nothing waiting on it, so it cannot be behind whatever the
 * rest of the payload says.
 *
 * This deliberately outranks `delay_tier`. `delay_seconds` is a reading of the
 * last item processed and survives the queue draining, so a tier computed from it
 * can describe work that already finished. Reporting "Major delay" on a category
 * with nothing pending would lift it into "Services in distress" and mark its
 * message type delayed, which is the false alarm this work exists to remove.
 * Everywhere else the backend's tier wins.
 */
function resolveTier(category: DelayStatusInput): DelayTier {
  if (category.pending === 0) return 'normal';
  if (isDelayTier(category.delay_tier)) return category.delay_tier;
  return deriveDelayTier(category.delay_seconds);
}

/**
 * The status a category reports. `delay_tier` is authoritative — the backend
 * decides severity against thresholds it scales per workspace, and the schema
 * says outright to render it rather than deriving one from `delay_seconds`.
 */
export function delayStatus(category: DelayStatusInput): {
  label: string;
  /** Raw colour, for StatusIndicator and anything else keyed on green/yellow/red. */
  color: 'green' | 'yellow' | 'red';
  /** Semantic colour, for Badge. Always the same severity as `color`. */
  badgeColor: 'success' | 'caution' | 'critical';
  tier: DelayTier;
} {
  const tier = resolveTier(category);
  const color = tierColor(tier, category.delay_cause);
  return { label: DELAY_STATUS_LABELS[tier], color, badgeColor: DELAY_BADGE_COLORS[color], tier };
}

/**
 * Why a category is behind, as attributed by the backend rather than inferred
 * here. Absent whenever the tier is normal, and also when there was no
 * throughput baseline to attribute the delay against — an unattributed delay is
 * a real state, not an error.
 */
export type DelayCauseValue = 'volume' | 'platform' | 'rate_limited';

/**
 * One short sentence per cause, and only `platform` is written as a problem.
 *
 * `volume` is the common case on a large workspace and means the queues are
 * keeping pace with what that workspace is sending. Reporting it as a fault is
 * exactly the false alarm this work exists to stop, so it reads as an
 * explanation rather than a warning.
 */
export const DELAY_CAUSE_NOTES: Record<DelayCauseValue, string> = {
  volume: 'The service is working through high volume, which is expected and clears as processing continues.',
  rate_limited: 'Delivery is paced at a controlled rate, so no action is needed.',
  platform: 'Throughput has dropped below what this workspace normally sustains.',
};

/**
 * Where each source kind is configured.
 *
 * `csv_import` uses the manual imports route, keyed by the import id `source_id` carries.
 *
 * `cdp_source` is a hydra-owned TanStack route rather than a manual one, which is why it is
 * carried as a target for the component to resolve instead of a string built here.
 *
 * `sql_sync` needs two fields, not one. Its page is `integrations/sql-sync/:type/:id`, and the
 * type segment is not optional: tested against a live workspace, the id alone does not reach the
 * import. The database type arrives as `source_subtype`, which the backend leaves empty when it
 * could not read the sync's database, so a sync without one stays a name. Half a URL is a wrong
 * link, and a wrong link is worse than no link.
 */
type DelayAttributionTarget =
  /** A manual/embedded route, resolvable to a string without the router. */
  | { kind: 'href'; href: string }
  /** A hydra-owned TanStack route; the caller resolves it through the router. */
  | {
      kind: 'route';
      to: '/workspaces/$workspaceId/pipelines/sources/$sourceId/data-in';
      params: { workspaceId: number; sourceId: number };
    };

type DelayAttributionInput = {
  label: string;
  share: number;
  source_kind?: string | null;
  source_id?: number | null;
  /** Qualifies the kind where its page needs more than an id. Today only `sql_sync` has one. */
  source_subtype?: string | null;
};

/** The attribution sentence, split so the label can be linked in place. */
export type DelayAttributionNote = {
  before: string;
  label: string;
  after: string;
  /** Null whenever the source cannot be pointed at, which is an ordinary state. */
  target: DelayAttributionTarget | null;
};

/**
 * The database types a SQL sync's page can be addressed by.
 *
 * Taken from the same constant the integrations directory reads, rather than re-listed, so the
 * two cannot drift. It mirrors the `SqlImportDatabase` enum in the Journeys app
 * (`app/utils/sql-import.ts`), which is what the integrations page builds these links from:
 * `urlFor('sql-imports.import.index', type, id)` in `pods/integrations/all/controller.ts`.
 *
 * Checked rather than trusted, so an unrecognised wire value renders as a name instead of a URL
 * to a page that does not exist. A new database type shows up here as a missing link, which is
 * the safe direction to be wrong in.
 */
const SQL_SYNC_DATABASE_TYPES = new Set<string>(SQL_IMPORT_DATABASES);

// Every path here is the full one, composed from each parent mount: route names are not paths.
function attributionTarget(attribution: DelayAttributionInput, workspaceId: number): DelayAttributionTarget | null {
  const { source_kind: kind, source_id: id, source_subtype: subtype } = attribution;
  if (kind == null || id == null || workspaceId <= 0) return null;

  switch (kind) {
    case 'sql_sync':
      // The one kind whose page is addressed by two segments. Without a database type this build
      // recognises there is no URL to build, so the sync is named instead of linked.
      if (subtype == null || !SQL_SYNC_DATABASE_TYPES.has(subtype)) return null;

      return {
        kind: 'href',
        href: generatePath('/workspaces/:workspaceId/journeys/integrations/sql-sync/:type/:sqlImportId', {
          workspaceId,
          type: subtype,
          sqlImportId: id,
        }),
      };
    case 'csv_import':
      return {
        kind: 'href',
        href: generatePath('/workspaces/:workspaceId/journeys/imports/:importId', { workspaceId, importId: id }),
      };
    case 'cdp_source':
      return {
        kind: 'route',
        to: '/workspaces/$workspaceId/pipelines/sources/$sourceId/data-in',
        params: { workspaceId, sourceId: id },
      };
    // Any kind this build does not know. The backend documents plain text as the expected
    // rendering for a kind the frontend has no route for.
    default:
      return null;
  }
}

/**
 * Names the source behind an import backlog, and points at it where it can be pointed at.
 *
 * `share` is a fraction of the sampled backlog, and the backend only sends this at all when one
 * source clears a dominance threshold, so the number is always a majority. It is rounded and
 * hedged rather than printed precisely: it comes from a sample, and a false-precision "71.4%"
 * would invite a reader to reconcile it against numbers that were never meant to agree.
 */
/**
 * The attributed share as whole percent, or null when it is not worth printing.
 *
 * The share comes from a sample, so it is rounded rather than shown to a decimal that invites the
 * reader to reconcile it against numbers that were never meant to agree. Rounding to zero, or a
 * share that is not a number at all, drops it: "about 0% of it" says less than nothing.
 */
export function attributionSharePercent(attribution: DelayAttributionInput | null | undefined): number | null {
  if (attribution == null) return null;

  const percent = Math.round(attribution.share * 100);
  return Number.isFinite(percent) && percent > 0 ? percent : null;
}

/**
 * What to call the work the share is a share of.
 *
 * "Backlog" is a complaint. It reads as something to fix, which is right for a category that is
 * behind and wrong for one that is keeping up — and the backend is moving to attributing on
 * pending rather than on delay, so healthy categories will start carrying an attribution. Calling
 * a healthy queue a backlog would manufacture a problem the page has spent this whole effort
 * learning not to invent.
 */
export function attributedWorkNoun(tier: DelayTier): string {
  return tier === 'normal' ? 'queue' : 'backlog';
}

/**
 * The attribution sentence for a category, split so the label can be linked in place.
 *
 * Takes the tier rather than defaulting one, so every surface has to say whether the category it
 * is describing is behind. Getting that wrong is the difference between reporting work and
 * reporting a problem.
 */
export function buildDelayAttributionNote(
  attribution: DelayAttributionInput | null | undefined,
  workspaceId: number,
  tier: DelayTier,
): DelayAttributionNote | null {
  if (attribution == null || isEmptyString(attribution.label)) return null;

  const percent = attributionSharePercent(attribution);
  const after = percent != null ? `, about ${percent}% of it.` : '.';

  return {
    before: `Most of this ${attributedWorkNoun(tier)} is from `,
    label: attribution.label,
    after,
    target: attributionTarget(attribution, workspaceId),
  };
}

/**
 * The category an alert was raised for, when the response still carries it.
 *
 * Alerts carry `category` and `surface` since services#24575. Both are needed: the same category
 * name can appear on both the processing and egress sides, so resolving on name alone could read
 * the wrong side's numbers.
 *
 * Returns the whole category rather than just its attribution, because a caller naming the source
 * also has to know whether that source is behind before it can word the sentence.
 */
export function resolveAlertCategory(
  alert: Pick<EnvHealthAlert, 'category' | 'surface'>,
  data: EnvHealthResponse | undefined,
): EnvHealthProcessingCategory | undefined {
  if (data == null || alert.category == null || alert.surface == null) return undefined;

  const categories = alert.surface === 'egress' ? data.egress.categories : data.processing.categories;
  return categories.find((category) => category.name === alert.category);
}

/** Only `platform` earns a warning treatment; the other two are ordinary states. */
export const DELAY_CAUSE_TONE: Record<DelayCauseValue, 'neutral' | 'caution'> = {
  volume: 'neutral',
  rate_limited: 'neutral',
  platform: 'caution',
};

export function formatDelayText(delaySeconds: number | null | undefined): string {
  if (delaySeconds == null || delaySeconds < 10) return '';
  if (delaySeconds < 60) return 'Slightly behind';
  const mins = Math.round(delaySeconds / 60);
  if (mins < 60) return `${mins} min behind`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  const duration = remainingMins > 0 ? `${hours} hr ${remainingMins} min` : `${hours} hr`;
  return `${duration} behind`;
}

const HEALTH_HEADER_STATUS_CONFIG: Record<EnvHealthResponse['health'], { heading: string; description: string }> = {
  healthy: {
    heading: 'Healthy',
    description: 'Everything is running smoothly. No issues detected.',
  },
  busy: {
    heading: 'Busy',
    description: 'Your workspace is processing a higher volume than usual.',
  },
  degraded: {
    heading: 'Slow performance',
    description: 'Your workspace has pending issues that may be impacting performance.',
  },
};

function countSeverities(data: EnvHealthResponse): { high: number; medium: number; low: number } {
  let high = 0;
  let medium = 0;
  let low = 0;

  for (const alert of data.alerts) {
    if (alert.severity === 'error') {
      high++;
    } else if (alert.severity === 'caution') {
      medium++;
    } else if (alert.severity === 'info') {
      low++;
    }
  }

  return { high, medium, low };
}

export function getHealthSummary(
  data: EnvHealthResponse | undefined,
  isIdentifiersMigrationInProgress: boolean,
  peoplePlural = 'people',
) {
  if (data == null) {
    return {
      heading: HEALTH_HEADER_STATUS_CONFIG.healthy.heading,
      severities: { high: 0, medium: 0, low: 0 },
      hasIssues: false,
      info: [HEALTH_HEADER_STATUS_CONFIG.healthy.description],
    };
  }

  const { heading } = HEALTH_HEADER_STATUS_CONFIG[data.health];
  const severities = countSeverities(data);
  const hasIssues = severities.high > 0 || severities.medium > 0;

  let { description } = HEALTH_HEADER_STATUS_CONFIG[data.health];
  if (data.health === 'healthy' && severities.low > 0) {
    description = 'Everything is running smoothly, with some minor items to be aware of.';
  }

  return {
    heading,
    severities,
    hasIssues,
    info: [
      isIdentifiersMigrationInProgress
        ? `Identifiers migration in progress. All data processing related to ${peoplePlural} and events is stopped until the migration is complete. Updates to existing ${peoplePlural} are paused as well. Everything else will continue to work as usual.`
        : '',
      description,
    ].filter(Boolean),
  };
}

export interface HealthFetchNotice {
  variant: 'error' | 'caution';
  title: string;
  description: string;
}

/**
 * Decide what (if anything) to tell the user about a failed env_health fetch.
 *
 * TanStack splits query failures in two, and the dashboard has to treat them
 * differently:
 *
 *   - `isLoadingError` — the fetch failed with nothing in cache. There is no
 *     dashboard to show, so a hard error is the honest state.
 *   - `isRefetchError` — a *background* refresh failed but the previous payload
 *     is still in cache and still rendered. The dashboard works; it's just
 *     older than it should be. Showing a red "unable to load" banner over a
 *     fully-populated dashboard reads as an unexplained failure (AUD-4253).
 *
 * The heavy /health endpoint polls every 5 minutes and refetches on window
 * focus, so on a busy workspace the refetch case is by far the more common of
 * the two — and it is the one that was previously mislabelled as a hard error.
 */
export function getHealthFetchNotice(isLoadingError: boolean, isRefetchError: boolean): HealthFetchNotice | null {
  if (isLoadingError) {
    return {
      variant: 'error',
      title: 'Unable to load workspace performance',
      description: 'We could not fetch your workspace performance data. Try again in a moment.',
    };
  }

  if (isRefetchError) {
    return {
      variant: 'caution',
      title: 'Showing your last successful update',
      description:
        'We could not refresh workspace performance just now, so these numbers may be behind. They will update automatically on the next refresh.',
    };
  }

  return null;
}

export function collapseProcessingAlerts(alerts: EnvHealthAlert[]): EnvHealthAlert[] {
  const processingAlerts = alerts.filter((a) => a.action_type === 'processing_details');
  const otherAlerts = alerts.filter((a) => a.action_type !== 'processing_details');

  if (processingAlerts.length <= 2) {
    return alerts.toSorted((a, b) => DATA_ALERT_SEVERITY_RANK[a.severity] - DATA_ALERT_SEVERITY_RANK[b.severity]);
  }

  const highestSeverity = processingAlerts.some((a) => a.severity === 'error')
    ? 'error'
    : processingAlerts.some((a) => a.severity === 'caution')
      ? 'caution'
      : 'info';

  const collapsed: EnvHealthAlert = {
    severity: highestSeverity,
    title: 'Multiple queues behind',
    message: 'Some data and messages may be delayed',
    action_label: 'View details',
    action_type: 'processing_details',
  };

  return [collapsed, ...otherAlerts].sort(
    (a, b) => DATA_ALERT_SEVERITY_RANK[a.severity] - DATA_ALERT_SEVERITY_RANK[b.severity],
  );
}
